/**
 * The Export button and menu of a model change list.
 *
 * Lifted out of the Version Control screen so a comparison of two baselines
 * (issue #97, phase E) offers EXACTLY the same files - the three sections as CSV
 * and the whole list as Markdown - without a second copy of the menu to drift.
 * Which address each entry goes to is the caller's (`urlFor`), because the branch
 * list and a baseline comparison are different routes with the same query shape.
 */

import { useState } from 'react'
import Button from '@mui/material/Button'
import DownloadIcon from '@mui/icons-material/Download'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'

/** One shape per file. The three sections answer different questions and have different columns, so a single sheet holding all three would be mostly empty cells nobody can sort. */
const PARTS: [string, string][] = [
  ['requirements', 'Requirements changed (CSV)'],
  ['review', 'To re-review (CSV)'],
  ['elements', 'Every element (CSV)'],
]

export default function ChangesExportMenu({
  urlFor,
  disabled,
}: {
  /** Where one file lives; `part` is given for a CSV and left out for the Markdown. */
  urlFor: (fmt: 'csv' | 'md', part?: string) => string
  disabled?: boolean
}) {
  // The Export menu's anchor. Null means closed.
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)

  return (
    <>
      <Button
        size="small"
        startIcon={<DownloadIcon />}
        onClick={(event) => setAnchor(event.currentTarget)}
        disabled={disabled}
      >
        Export
      </Button>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {PARTS.map(([part, label]) => (
          <MenuItem
            key={part}
            component="a"
            href={urlFor('csv', part)}
            download
            onClick={() => setAnchor(null)}
          >
            {label}
          </MenuItem>
        ))}
        {/* The same block that goes into the merge request description, so a review
            pasted into a document and the record in GitLab cannot say different
            things. */}
        <MenuItem component="a" href={urlFor('md')} download onClick={() => setAnchor(null)}>
          The whole list (Markdown)
        </MenuItem>
      </Menu>
    </>
  )
}
